import { sanityClient } from './client';
import { PREDICTED_LINEUPS_SINGLETON_ID } from './predictedLineups';
import type { SanityArticleDocumentType } from './types';

export type SitemapDocumentType =
  | SanityArticleDocumentType
  | 'contentPage'
  | 'productUpdate'
  | 'predictedLineupsPage';

/**
 * What a sitemap entry needs from any document with a public URL. GROQ
 * returns null, never undefined, for a field a document lacks: content pages
 * have no author or editorial dates, and the predicted lineups page, a
 * singleton at a fixed path, has no slug either.
 */
export interface SanitySitemapDocument {
  _type: SitemapDocumentType;
  title: string | null;
  slug: string | null;
  authorSlug: string | null;
  publishedAt: string | null;
  lastUpdatedAt: string | null;
  _createdAt: string;
  _updatedAt: string;
  canonicalUrl: string | null;
  isHiddenFromSearch: boolean | null;
}

/** Google News reads at most this many articles from one news sitemap. */
export const MAX_NEWS_SITEMAP_ARTICLES = 1000;

const SITEMAP_DOCUMENT_PROJECTION = `{
    _type,
    title,
    "slug": slug.current,
    "authorSlug": author->slug.current,
    publishedAt,
    lastUpdatedAt,
    _createdAt,
    _updatedAt,
    "canonicalUrl": seo.canonicalUrl,
    "isHiddenFromSearch": seo.isHiddenFromSearch
  }`;

// Hidden and canonicalised documents are filtered in code rather than here,
// so the rules that decide what search engines see stay unit-testable. A
// product update without a category has no page, so it is left out here.
export const sitemapDocumentsQuery = `
  *[((_type in ["articlePage", "teamReveal", "contentPage"]
      || (_type == "productUpdate" && defined(category)))
    && defined(slug.current))
    || _id == "${PREDICTED_LINEUPS_SINGLETON_ID}"]
    | order(_createdAt desc) ${SITEMAP_DOCUMENT_PROJECTION}
`;

// A publish date set in the future would otherwise be announced early and
// stay listed until two days after it.
export const articlesPublishedSinceQuery = `
  *[_type in ["articlePage", "teamReveal"] && defined(slug.current)
    && dateTime(coalesce(publishedAt, _createdAt)) > dateTime($since)
    && dateTime(coalesce(publishedAt, _createdAt)) <= dateTime(now())
  ] | order(coalesce(publishedAt, _createdAt) desc) [0...$limit] ${SITEMAP_DOCUMENT_PROJECTION}
`;

export const getSitemapDocuments = (): Promise<SanitySitemapDocument[]> =>
  sanityClient.fetch<SanitySitemapDocument[]>(sitemapDocumentsQuery);

export const getArticlesPublishedSince = (
  since: string
): Promise<SanitySitemapDocument[]> =>
  sanityClient.fetch<SanitySitemapDocument[]>(articlesPublishedSinceQuery, {
    since,
    limit: MAX_NEWS_SITEMAP_ARTICLES
  });
